import type WaButton from "@web.awesome.me/webawesome-pro/dist/components/button/button.js";
import type WaInput from "@web.awesome.me/webawesome-pro/dist/components/input/input.js";
import htmx from "htmx.org";
import { css, html } from "lit";
import { z } from "zod";
import { checkNotNil } from "../../check-not-nil";
import { type FormSubmissionHandler, formSubmissionHandler } from "../../form";
import { JsonBootstrapper } from "../../handlers/json-bootstrapper";
import { DatetimeString } from "../../schemas/datetime";
import {
  type ParserSchema,
  type ParserType,
  parser,
} from "../../schemas/parser";
import { Tags } from "../../schemas/tag";
import { TaskChecklist } from "../../schemas/task";
import { ValidationError } from "../../schemas/validation-error";
import { hostStyles } from "../../styles/host.styles";
import type { WmChecklist } from "../checklist/checklist";
import { WmElement } from "../element";

// Hydration payload should only matter once, and not be treated as
// a property methinks.
type Payload = ParserType<typeof Payload>;
const Payload = parser(
  z.strictObject({
    id: z.string(),
    checklist: TaskChecklist.schema,
    createdAt: DatetimeString.schema,
    description: z.string(),
    dueBy: DatetimeString.schema.optional(),
    doneAt: DatetimeString.schema.optional(),
    selectedTags: Tags.schema,
  }),
);

export class WmTaskEdit extends WmElement {
  static readonly TAG_NAME = "wm-task-edit";

  static styles = [
    hostStyles,
    css`
      form {
        display: flex;
        flex-direction: column;
        align-items: stretch;
        justify-content: flex-start;
        gap: var(--wa-space-m);
      }

      wa-button[type="submit"] {
        align-self: start;
      }
    `,
  ];

  #formSubmit?: FormSubmissionHandler;
  #payload: Payload;
  #jsonBootstrapper: JsonBootstrapper<ParserSchema<typeof Payload>>;

  constructor() {
    super();
    this.#formSubmit = undefined;
    this.#payload = {
      id: "",
      checklist: [],
      createdAt: new Date(0).toISOString(),
      description: "",
      selectedTags: [],
    };
    this.#jsonBootstrapper = new JsonBootstrapper({
      schema: Payload.schema,
      callback: (v) => {
        this.#payload = v;
        this.#formSubmit = formSubmissionHandler(`/app/tasks/${v.id}`, {
          method: "PUT",
        })
          .on("pre:response", () => (this.#querySubmitButton().loading = true))
          .on("response", (r) => this.#handleResponse(r))
          .on("pre:response", () => (this.#querySubmitButton().loading = false))
          .on("error", (err) => {
            this.logger.error("unable to PUT /app/tasks", err);
          });
        this.requestUpdate();
      },
      logger: this.logger,
    });
  }

  protected override render() {
    const availableTags = this.#payload.selectedTags ?? [];
    const selectedTagIds = availableTags.map((t) => t.id);

    return html`
      <slot @slotchange=${(e: Event) => this.#jsonBootstrapper.slotChanged(e)}></slot>
      <form @submit=${{ handleEvent: this.#formSubmit }}>
        <wm-output class="id" label="Id">${this.#payload.id}</wm-output>
        <wm-date-output label="Created at" value=${this.#payload.createdAt}></wm-date-output>
        <wa-input
          label="Description"
          name="description"
          required
          .value=${this.#payload.description}
          @input=${this.#handleDescriptionInput}>
        </wa-input>
        <wm-datetime-input name="due_by" label="Due by" .value=${this.#payload.dueBy}>
        </wm-datetime-input>
        <wm-checklist name="checklist" label="Checklist" .checklist=${this.#payload.checklist}>
        </wm-checklist>
        <wm-tags-combobox
          name="tags"
          .availableTags=${availableTags}
          .selectedTagIds=${selectedTagIds}>
        </wm-tags-combobox>
        <wa-button variant="success" type="submit">Modify</wa-button>
      </form>
    `;
  }

  async #handleResponse(response: Response) {
    // Handle 422 with JSON serialized error messages.
    if (response.status === 422) {
      const validationError = ValidationError.parse(await response.json());
      const { path, reason } = validationError.error;

      const checklistPathRegex = /checklist\[(\d+)\].description/;

      if (path === "description") {
        const descriptionInput = this.#queryDescriptionInput();
        descriptionInput.setCustomValidity(reason);
        descriptionInput.reportValidity();
      } else if (checklistPathRegex.test(path)) {
        const captures = checkNotNil(checklistPathRegex.exec(path));
        const indexString = captures[1];
        const index = parseInt(indexString, 10);
        const checklist = this.#queryChecklist();
        checklist.reportError(index, reason);
      } else {
        this.logger.warn(
          "unknown invalid field from 422 response: %s",
          validationError.error.path,
          validationError,
        );
      }
    } else {
      // The equivalent of using HX-Location: "/app/tasks"
      return htmx.ajax("GET", "/app/tasks", { push: "true" });
    }
  }

  #handleDescriptionInput(e: InputEvent) {
    const input = checkNotNil(e.target) as WaInput;
    input.setCustomValidity("");
  }

  #queryChecklist(): WmChecklist {
    return this.queryNotNil("wm-checklist");
  }

  #queryDescriptionInput(): WaInput {
    return checkNotNil(this.renderRoot.querySelector("wa-input"));
  }

  #querySubmitButton(): WaButton {
    return this.queryNotNil(`wa-button[type="submit"]`);
  }
}

customElements.define(WmTaskEdit.TAG_NAME, WmTaskEdit);

declare global {
  interface HTMLElementTagNameMap {
    [WmTaskEdit.TAG_NAME]: WmTaskEdit;
  }
}
